// Login.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  FormGroup,
  Stack,
  TextField,
} from "@mui/material";
import logo from "../../assets/images/admin-icon-2/Logo.png";
import playerdex from "../../assets/images/admin-icon-2/playerdex.png";
import {
  sendSignInLink,
  completeSignInWithEmailLink,
  getStorage,
} from "../../auth/utils";
import { GET_ME } from "../../graphql/userGraphql";
import { AuthContext } from "../../context/AuthContext";
import { SnackBarContext } from "../../context/SnackBarContext";
import { useTranslation } from "../../context/Internationalization";
import TranslationToggle from "../../Components/TranslationToggle";
import useLoginStyles from "./Login.styles";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { emailRegex } from "../../utility/Utils";
import { handleFirebaseAuthError } from "../../auth/firebaseErrorHandler";

const initialState = {
  email: "",
  status: "idle",
};

export const Login = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const snackBar = useContext(SnackBarContext);
  const classes = useLoginStyles();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const isDev = process.env.REACT_APP_NODE_ENV === "development";

  const isFormValid = form.email?.trim() !== "";

  useEffect(() => {
    const token = localStorage.getItem("token");
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams.get("mode");
    const oobCode = urlParams.get("oobCode");

    if (mode === "signIn" && oobCode) {
      setLoading(true);

      // If the user clicked the magic link, complete login
      completeSignInWithEmailLink()
        .then(({ user, idToken }) => {
          if (user) {
            getStorage().setItem("token", idToken);
            getStorage().setItem("tokenLastValidity", Date.now().toString());

            // Fetch user details
            axiosGraphQL({ query: GET_ME })
              .then((response) => {
                const userData = response.data.data.me?.user;
                if (!userData) throw new Error(t("no_user_found"));

                if (userData?.roles?.includes("admin")) {
                  auth.setAuthState({ user: userData, token: idToken });
                  localStorage.setItem("user", JSON.stringify(userData));

                  snackBar.setOptions({
                    alertMessage: t("login_success"),
                    alertSeverity: "success",
                  });
                  snackBar.setSnackbarOpen(true);
                  navigate("/super-admin/clubs", { replace: true });
                } else {
                  throw new Error(t("unauthorized_access"));
                }
              })
              .catch((error) => {
                errorLog(error, "Login", "useEffect", "GET_ME");
                auth.logout();
                snackBar.setOptions({
                  alertMessage: error.message || t("error_fetching_user"),
                  alertSeverity: "error",
                });
                snackBar.setSnackbarOpen(true);
              })
              .finally(() => setLoading(false));
          }
        })
        .catch((error) => {
          errorLog(error, "Login", "useEffect", "completeSignInWithEmailLink");
          snackBar.setOptions({
            alertMessage: error.message || t("unknown_error"),
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
          setLoading(false);
        });
    } else if (token) {
      setLoading(true);

      // If the user is already logged in, fetch user details
      axiosGraphQL({ query: GET_ME })
        .then((response) => {
          const user = response.data.data.me?.user;
          if (!user) throw new Error(t("no_user_found"));

          if (user?.roles?.includes("admin")) {
            auth.setAuthState({
              user,
              token,
              club: JSON.parse(getStorage().getItem("club")),
            });
            setForm((prev) => ({ ...prev, status: "resolve" }));
            navigate("/super-admin/clubs", { replace: true });
          } else {
            setForm((prev) => ({ ...prev, status: "rejected" }));
            throw new Error(t("unauthorized_access"));
          }
        })
        .catch((error) => {
          errorLog(error, "Login", "useEffect", "GET_ME");
          auth.logout();
          snackBar.setOptions({
            alertMessage: error.message || t("error_fetching_user"),
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        })
        .finally(() => setLoading(false));
    }
  }, [location.search]);

  const updateField = ({ target: { name, value } }) => {
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = form.email?.trim()?.toLowerCase();
    const language = getStorage().getItem("language") || "gr";

    if (!emailRegex.test(email)) {
      snackBar.setOptions({
        alertMessage: t("invalid_email"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      return;
    }

    setForm({ ...form, status: "pending" });

    try {
      await sendSignInLink(email, language);
      snackBar.setOptions({
        alertMessage: t("email_link_sent"),
        alertSeverity: "info",
      });
      snackBar.setSnackbarOpen(true);
      setForm({ ...form, status: "email_sent" });
    } catch (error) {
      errorLog(error, "Login", "handleSubmit", "sendSignInLink");
      const friendlyMessage = handleFirebaseAuthError(error, t);

      snackBar.setOptions({
        alertMessage: friendlyMessage,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);

      setForm(
        error?.code === "auth/too-many-requests"
          ? { ...form, status: "locked" }
          : { ...initialState },
      );

      if (error?.code === "auth/too-many-requests") {
        setTimeout(() => setForm({ ...form, status: "idle" }), 60000);
      }
    }
  };

  return (
    <Box component="div" className={classes.root}>
      {loading ? (
        <Box className={classes.loaderContainer}>
          <CircularProgress color="primary" />
          <p>{t("signing_in")}</p>
        </Box>
      ) : (
        <Box component="div" className={classes.formContainer}>
          {/* Logo Section */}
          <Box className={classes.logoContainer}>
            <Badge
              invisible={!isDev}
              color="error"
              badgeContent="DEV"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.55rem",
                  fontWeight: "bold",
                  padding: "0 4px",
                  height: "14px",
                  minWidth: "22px",
                  borderRadius: "4px",
                },
              }}
            >
              <Box>
                <img
                  className={classes.logoImage}
                  src={logo}
                  alt="PlayerDex logo"
                />
              </Box>
            </Badge>
            <Box>
              <img
                className={classes.playerdexLogo}
                src={playerdex}
                alt="PlayerDex"
              />
            </Box>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.descriptionText}
          >
            {t("login_page_msg")} <TranslationToggle />
          </Stack>

          {/* Form */}
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Email Field */}
            <FormGroup className={classes.formGroup}>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={form.email}
                onChange={updateField}
                placeholder={t("email_placeholder")}
                required
                slotProps={{
                  input: {
                    className: classes.inputField,
                  },
                }}
              />
            </FormGroup>

            {/* Sign-In Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              className={classes.signInButton}
              disabled={!isFormValid || form.status === "email_sent"}
            >
              {form.status === "email_sent"
                ? t("check_email")
                : t("send_magic_link")}
            </Button>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default Login;
